import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  input,
  output,
  viewChild,
  ElementRef,
  computed,
} from '@angular/core';
import { CardComponent, CardContentComponent, IconComponent } from '@gains-com/ui-angular';
import { RedemptionDialogHeaderComponent } from '../redemption-dialog-header/redemption-dialog-header.component.js';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, filter, from, switchMap } from 'rxjs';
import { RedemptionPaymentMethod, MINIMUM_REDEMPTION_AMOUNT } from '../../common';
import { PaymentMethodsService } from '../../../../core/payment-methods.js';
import { UserViewService } from '../../../../core/view-states/user-view.service';
import {
  CasinoSettingKeyId,
  decimal,
  PaymentMethodEntityTypeId,
  PaymentProviderAccountEntityTypeId,
} from '@gains-com/shared';
import { MetadataViewService } from '../../../../core/view-states/metadata-view.service.js';
import { PaymentProviderAccountsViewService } from '../../../../core/view-states/payment-provider-accounts-views.service.js';
import { AuthService } from '../../../../core/auth.service.js';
import { PaymentMethodsViewService } from '../../../../core/view-states/payment-methods-view.service.js';
import { AccountLimitsViewService } from '../../../../core/view-states/account-limits-view.service.js';
import { MatButton } from '@angular/material/button';
import { HideOnCasinoSettingDisabledDirective } from '../../../../directives/hide-on-casino-setting-disabled.directive.js';
import { SweepsVisibleDirective } from '../../../../directives/sweeps-visible.directive.js';

interface RedemptionMethodForm {
  amount: FormControl<string | null>;
}

@Component({
  selector: 'app-redemption-methods',
  imports: [
    CardComponent,
    CardContentComponent,
    RedemptionDialogHeaderComponent,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    MatError,
    IconComponent,
    MatButton,
    HideOnCasinoSettingDisabledDirective,
    SweepsVisibleDirective,
  ],
  templateUrl: './redemption-methods.component.html',
  styleUrls: ['../styles.scss', './redemption-methods.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RedemptionMethodsComponent {
  private readonly _userView = inject(UserViewService);
  private readonly _paymentMethods = inject(PaymentMethodsService);
  private readonly _metadataView = inject(MetadataViewService);
  private readonly _paymentProviderAccountsViewService = inject(PaymentProviderAccountsViewService);
  private readonly _auth = inject(AuthService);
  private readonly _paymentMethodsViewService = inject(PaymentMethodsViewService);
  private readonly _accountLimitsView = inject(AccountLimitsViewService);

  dailyRedemptionLimits = toSignal(this._accountLimitsView.dailyRedemptionLimits$);

  userState = toSignal(this._userView.state$);
  fiatRedemptionEnabledPlatform = toSignal(
    this._metadataView.casinoSettingValue$<boolean>(CasinoSettingKeyId.RedemptionsFiat),
  );
  cryptoRedemptionEnabledPlatform = toSignal(
    this._metadataView.casinoSettingValue$<boolean>(CasinoSettingKeyId.RedemptionsCrypto),
  );
  fiatRedemptionEnabledUser = computed(
    () =>
      this.userState()?.redemptions_fiat_enabled &&
      this.userState()?.country_id === 'USA' &&
      this.userState()?.country_enabled,
  );
  cryptoRedemptionEnabledUser = computed(() => this.userState()?.redemptions_crypto_enabled);
  paymentProviderAccounts = toSignal(this._paymentProviderAccountsViewService.paymentProviderAccounts$);
  paymentMethods = toSignal(this._paymentMethodsViewService.visiblePaymentMethods$);

  coinflowAccount = computed(() =>
    this.paymentProviderAccounts()?.find(
      (ppa) =>
        ppa.payment_provider_account_entity_type_id === PaymentProviderAccountEntityTypeId.CoinflowUserForWithdrawals,
    ),
  );

  coinflowStatus = computed(() => this.coinflowAccount()?.payment_provider_account_approval_status ?? 'none');

  coinflowBlocked = computed(() => !!this.coinflowAccount()?.payment_provider_blocked);

  coinflowStatusPending = computed(
    () =>
      !this.coinflowBlocked() &&
      ((Array.isArray(this.paymentProviderAccounts()) && this.coinflowStatus() === 'none') ||
        this.coinflowStatus() === 'pending'),
  );

  coinflowNeedsSync = computed(() => this.coinflowStatusPending() || this.coinflowBlocked());

  coinflowUnavailableNotice = computed<{ title: string; body: string } | null>(() => {
    if (this.coinflowBlocked()) {
      return {
        title: 'Card and bank withdrawals unavailable',
        body: 'Our payment processor is unable to offer debit card, bank account or Venmo withdrawals for your account. You can still redeem to a crypto wallet, or contact support if you have questions.',
      };
    }

    if (this.coinflowStatus() === 'rejected') {
      return {
        title: 'Verification Failed',
        body: 'You can still redeem to a crypto wallet for now. For the latest on your identity check with our payment processor, please contact support.',
      };
    }

    return null;
  });

  venmoConnected = computed(
    () =>
      !!this.paymentMethods()?.find(
        (pm) =>
          pm.payment_method_entity_type_id === PaymentMethodEntityTypeId.CoinflowVenmoAccount &&
          pm.approved_by_payment_provider,
      ),
  );

  // A processor block also locks fiat redemptions server-side; keep the rows and
  // the blocked notice on screen so the player sees why rather than no fiat options.
  coinflowHidden = computed(() => !this.fiatRedemptionEnabled() && !this.coinflowBlocked());

  coinflowDisabled = computed(
    () => !this.fiatRedemptionEnabled() || this.coinflowBlocked() || this.coinflowStatus() !== 'approved',
  );

  hasPlaythroughRequirement = computed(
    () =>
      !!this.userState()?.playthrough_requirement_id &&
      decimal(this.userState()?.playthrough_progress ?? 0).lt(decimal(this.userState()?.playthrough_requirement ?? 0)),
  );

  dailyLimitRemaining = computed(() => {
    const limits = this.dailyRedemptionLimits();
    if (!limits) {
      return 0;
    }

    const existingAmount = decimal(limits.daily_amount_usd ?? 0);
    const limit = decimal(limits.daily_redemptions_usd_limit ?? 0);

    return limit.sub(existingAmount).toNumber();
  });

  fiatRedemptionEnabled = computed(() => this.fiatRedemptionEnabledPlatform() && this.fiatRedemptionEnabledUser());
  cryptoRedemptionEnabled = computed(
    () => this.cryptoRedemptionEnabledPlatform() && this.cryptoRedemptionEnabledUser(),
  );

  amountInput = viewChild<ElementRef<HTMLInputElement>>('amountInput');
  amountInputParent = viewChild<ElementRef<HTMLDivElement>>('amountInputParent');

  step = input<number>(0);

  setRedemptionMethod = output<{
    amount: number;
    redemptionMethod: RedemptionPaymentMethod;
  }>();
  close = output<void>();
  coinflowToken = input<string | null>(null);

  loading = signal(true);
  prefixOffset = signal<number>(20);
  amountInputSet = signal(false);
  user = toSignal(this._userView.state$);

  minimumRedemptionAmount = MINIMUM_REDEMPTION_AMOUNT;

  redemptionMethodForm = new FormGroup<RedemptionMethodForm>({
    amount: new FormControl<string | null>(null),
  });

  measureWidth(value: string | number) {
    const el = this.amountInput()?.nativeElement;
    if (!el) return 0;

    const font = getComputedStyle(el).font;
    if (!font) return 0;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 0;
    context.font = font;

    return context.measureText(value.toString()).width;
  }

  amountKeydown(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (event.key === 'Backspace' || event.key === 'Delete') {
      value = value.slice(0, -1);
    } else if ((value + event.key).match(/^(0|[1-9][0-9]*)(\.([0-9]){0,2})?$/g)) {
      value = value + event.key;
    }

    this.redemptionMethodForm.controls.amount.setValue(value);

    this.validateAmount(value);

    const maxOffset = decimal(this.amountInputParent()?.nativeElement.offsetWidth ?? 0)
      .div(2)
      .round()
      .sub(20)
      .toNumber();

    const offset =
      decimal(this.measureWidth(value ?? '0'))
        .div(2)
        .round()
        .toNumber() || 20;

    this.prefixOffset.set(Math.min(offset, maxOffset));

    this.amountInputSet.set(value !== null && value !== '');

    event.preventDefault();
  }

  private validateAmount(value: string) {
    if (!value) {
      this.redemptionMethodForm.controls.amount.setErrors(null);
      return;
    }

    const maxBalanceError = decimal(value).gt(decimal(this.user()?.sc_balance ?? 0).toNumber());
    const maxDailyLimitError = decimal(value).gt(decimal(this.dailyLimitRemaining()).toNumber());
    const minError = decimal(value).lt(decimal(this.minimumRedemptionAmount));

    const errors: Record<string, boolean> = {};
    if (maxBalanceError) errors['maxBalance'] = true;
    if (maxDailyLimitError) errors['maxDailyLimit'] = true;
    if (minError) errors['min'] = true;

    this.redemptionMethodForm.controls.amount.setErrors(Object.keys(errors).length ? errors : null);
  }

  onAmountInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Strip characters that don't match the allowed pattern
    const sanitized = value.match(/^(0|[1-9][0-9]*)(\.([0-9]){0,2})?/)?.[0] ?? '';
    if (sanitized !== value) {
      value = sanitized;
      input.value = value;
      this.redemptionMethodForm.controls.amount.setValue(value);
    }

    this.validateAmount(value);

    const maxOffset = decimal(this.amountInputParent()?.nativeElement.offsetWidth ?? 0)
      .div(2)
      .round()
      .sub(20)
      .toNumber();

    const offset =
      decimal(this.measureWidth(value ?? '0'))
        .div(2)
        .round()
        .toNumber() || 20;

    this.prefixOffset.set(Math.min(offset, maxOffset));
    this.amountInputSet.set(value !== null && value !== '');
  }

  select(method: RedemptionPaymentMethod) {
    this.redemptionMethodForm.markAllAsTouched();

    const value = this.redemptionMethodForm.controls.amount.value;
    if (!value) {
      this.redemptionMethodForm.controls.amount.setErrors({ min: true });
      this.amountInput()?.nativeElement.focus();
      return;
    }

    if (this.redemptionMethodForm.valid) {
      this.setRedemptionMethod.emit({
        amount: Number(value),
        redemptionMethod: method,
      });
    }
  }

  constructor() {
    // Fireblocks screen-open sync: when the crypto redemption method becomes
    // available, reconcile any address whitelisted on Fireblocks whose payment
    // method never arrived (missed webhook). The sync creates the PM and pushes it
    // via realtime. Declarative per the no-effect rule; switchMap cancels an
    // in-flight sync if availability flips, takeUntilDestroyed cleans up.
    toObservable(this.cryptoRedemptionEnabled)
      .pipe(
        filter(Boolean),
        switchMap(() =>
          from(
            this._paymentMethods.syncFireblocksRedemptionPaymentMethods().catch(() => {
              /* sync failure is non-critical; the user can retry manually */
            }),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe();

    // Refresh the user + Coinflow redemption methods while the withdrawal
    // account is still pending, or blocked so a block lifted at Coinflow clears
    // without a payout to re-check it. The accounts and the per-user fiat lock
    // the sync releases are re-fetched afterwards rather than trusting the
    // realtime update, which is best-effort. switchMap cancels a stale reload if
    // the state changes mid-request.
    toObservable(this.coinflowNeedsSync)
      .pipe(
        filter((needsSync) => needsSync),
        switchMap(() =>
          from(this._auth.forceUserReload()).pipe(
            switchMap(() => from(this._paymentMethods.syncCoinflowRedemptionPaymentMethods())),
            switchMap(() => from(this._paymentProviderAccountsViewService.syncPaymentProviderAccounts())),
            switchMap(() => from(this._userView.syncUserFeatures())),
            catchError(() => {
              // Sync failure is non-critical; the user can retry manually
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  protected readonly Number = Number;
}
